import { makeStyles } from '@map/theme';
import { alpha } from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import { useGetCountryName } from '@shared/hooks/displayNames';
import { useCurrentLocaleRegion } from '@shared/hooks/intl';
import { CountryCode, countryTelMap } from '@shared/intlConstants';
import { forceInputValueChange } from '@shared/util/dom';
import { NumeralSystem, convertNumerals } from '@shared/util/number';
import { findCountryByPhone } from '@shared/util/phoneNumber';
import CountryFlag from '@ui/CountryFlag';
import ChevronDownIcon from '@ui/Icon/library/ChevronDownIcon';
import Input from '@ui/formElements/Input';
import {
  ChangeEventHandler,
  ComponentPropsWithoutRef,
  ComponentType,
  FocusEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import TextFormField from './TextFormField';

const useStyles = makeStyles(({ borderRadius, palette, spacing }) => ({
  flagLabel: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    color: palette.neutrals[600],
    height: '2em',
    paddingLeft: spacing(2),
    marginLeft: spacing(-2),
    borderRadius: borderRadius('s'),
    '&:focus-within': {
      backgroundColor: alpha(palette.primary[300], 0.3),
    },
    '::after': {
      content: '""',
      marginLeft: spacing(2),
      borderLeft: `1px solid ${palette.neutrals[600]}`,
      height: '1em',
    },
  },
  flagSelect: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    opacity: 0,
  },
  flagImage: {
    borderRadius: borderRadius('s'),
    marginRight: spacing(2),
  },
}));

const ltrEmbed = '\u202a';
const endEmbed = '\u202c';

const strictCharsFilter = /[^+0-9\u06F0-\u06F9\u0660-\u0669]/g;
const looseCharsFilter = /[^+0-9\u06F0-\u06F9\u0660-\u0669\-() ]/g;

const CountryPickerAdornment: FC<{ value: string; onChange: any }> = ({ value, onChange }) => {
  const { classes } = useStyles();
  const getCountryName = useGetCountryName();
  const countryOptions = useMemo(
    () =>
      Object.entries(countryTelMap)
        .map(([c, t]) => [c, t, getCountryName(c)] as const)
        .sort(([, , a], [, , b]) => a.localeCompare(b))
        .map(([c, t, l]) => (
          <option key={c} value={c}>
            {`${l} (${ltrEmbed}+${t}${endEmbed})`}
          </option>
        )),
    [getCountryName],
  );

  return (
    <InputAdornment position="start">
      <label className={classes.flagLabel}>
        {/* eslint-disable-next-line react/forbid-elements */}
        <select className={classes.flagSelect} value={value} onChange={onChange}>
          <option key="none" value="" disabled />
          {countryOptions}
        </select>
        <CountryFlag className={classes.flagImage} code={value.toLowerCase()} />
        <ChevronDownIcon color="inherit" />
      </label>
    </InputAdornment>
  );
};

type BaseComponent = typeof TextFormField | typeof Input;

type Props = ComponentPropsWithoutRef<BaseComponent> & {
  value: string;
  strict?: boolean;
  TextFieldComponent?: ComponentType<any>;
};
const PhoneNumberField = (props: Props) => {
  const {
    value = '',
    strict = true,
    onChange,
    InputProps,
    TextFieldComponent = TextFormField,
    ...otherProps
  } = props;
  const localeCountry = useCurrentLocaleRegion();
  const [numeralSystem, setNumeralSystem] = useState<NumeralSystem>('western');
  const inputRef = useRef<HTMLInputElement>(null);
  const forceUpdateValue = (value: string) => forceInputValueChange(inputRef.current!, value);

  const [country, setCountry] = useState<CountryCode | ''>(() => {
    if (!value && localeCountry) {
      return localeCountry.toUpperCase();
    }
    return findCountryByPhone(value);
  });

  const prefix = value.slice(0, 5);
  useEffect(() => {
    if (value.length > 1) {
      const newCountry = findCountryByPhone(value);
      // this ensures that the user-selected country is preserved, when prefixes are the same
      if (countryTelMap[newCountry] && countryTelMap[newCountry] !== countryTelMap[country]) {
        setCountry(newCountry);
      }
    }
    // running this only when the first 5 characters change improves performance
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country, findCountryByPhone, prefix]);

  const handleChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      const enteredCharacters = (e.nativeEvent as InputEvent).data || '';
      let system = numeralSystem;
      if (enteredCharacters.match(/[0-9]/g)) {
        setNumeralSystem((system = 'western'));
      } else if (enteredCharacters.match(/[٠-٩]/g)) {
        setNumeralSystem((system = 'eastern'));
      } else if (enteredCharacters.match(/[۰-۹]/g)) {
        setNumeralSystem((system = 'persian'));
      }
      // only allow common phone number glyphs, including hindu-arabic and persian numerals
      const value = convertNumerals(
        e.target.value.replace(strict ? strictCharsFilter : looseCharsFilter, ''),
        system,
      );
      if (e.target.value !== value) {
        inputRef.current!.value = value;
      }
      onChange?.(e);
    },
    [numeralSystem, onChange, strict],
  );
  const handleFocus: FocusEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      const input = e.target;
      if (!input.value && country) {
        forceUpdateValue(convertNumerals(`+${countryTelMap[country]}`, numeralSystem));
      }
      setTimeout(() => {
        const end = input.value.length;
        input.setSelectionRange(end, end);
      }, 0);
    },
    [country, numeralSystem],
  );
  const handleBlur: FocusEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      if (e.target.value === convertNumerals(`+${countryTelMap[country]}`, numeralSystem)) {
        forceUpdateValue('');
      }
    },
    [country, numeralSystem],
  );

  const handleCountryChanged: ChangeEventHandler<HTMLSelectElement> = useCallback(
    (e) => {
      const newCountry = e.target.value;
      setCountry(newCountry);
      const newPrefix = convertNumerals(`+${countryTelMap[newCountry]}`, numeralSystem);
      if (!value.startsWith(newPrefix)) {
        const oldPrefix = convertNumerals(countryTelMap[country] || '', numeralSystem);
        const newValue = `${newPrefix}${value.replace(new RegExp(`^(\\+|00)${oldPrefix}`), '')}`;
        forceUpdateValue(convertNumerals(newValue, numeralSystem));
      }
      inputRef.current?.focus();
    },
    [country, numeralSystem, value],
  );

  return (
    <TextFieldComponent
      inputRef={inputRef}
      {...otherProps}
      characters="special"
      type="tel"
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      value={value}
      InputProps={{
        ...InputProps,
        startAdornment: <CountryPickerAdornment value={country} onChange={handleCountryChanged} />,
      }}
    />
  );
};

export default PhoneNumberField;
